import React, { useEffect, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import {
  Button,
  Card,
  DatePicker,
  Empty,
  Form,
  Modal,
  Radio,
  Segmented,
  Select,
  Space,
  Tag,
  Typography
} from "antd";
import { PlusOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import SearchBarComp from "../../../commons/SearchBar/SearchBar";
import { generalErrorAlert } from "../../../commons/GeneralErrorAlert";
import PageToolbar from "../../../design-system/PageToolbar";
import ResponsiveTable from "../../../design-system/ResponsiveTable";
import { useIsMobile } from "../../../design-system/useMediaQuery";
import { formatDay } from "../workReports/format";
import { createAcceptance, listAcceptances } from "./api";
import { ACCEPTANCE_COLORS, acceptanceStatusLabels, basisLabels } from "./labels";

export const NewAcceptanceModal = ({ open, meta, project, onClose }) => {
  const { t } = useTranslation("dashboard");
  const history = useHistory();
  const [form] = Form.useForm();
  const [busy, setBusy] = useState(false);
  const projectId = Form.useWatch("project", form);

  useEffect(() => {
    if (open)
      form.setFieldsValue({
        date: dayjs(),
        project: project || undefined,
        offer: undefined,
        scope: "full"
      });
  }, [open]);

  const offers = (meta?.offers || []).filter(
    o => !projectId || !o.project || o.project === projectId
  );

  const submit = async values => {
    setBusy(true);
    try {
      const res = await createAcceptance({
        date: values.date.format("YYYY-MM-DD"),
        project: values.project || null,
        offer: values.offer || null,
        scope: values.scope,
        ...(values.client ? { client: values.client } : {})
      });
      history.push(`/user/warranty/acceptances/${res.data.data._id}`);
    } catch (error) {
      generalErrorAlert(error);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      rootClassName="warranty-page"
      open={open}
      title={t("New acceptance protocol")}
      okText={t("Create")}
      cancelText={t("Cancel")}
      confirmLoading={busy}
      onOk={() => form.submit()}
      onCancel={onClose}
    >
      <Form form={form} layout="vertical" onFinish={submit}>
        <Form.Item name="date" label={t("Date of the acceptance")} rules={[{ required: true }]}>
          <DatePicker className="wty-full" format="DD.MM.YYYY" allowClear={false} />
        </Form.Item>
        <Form.Item name="project" label={t("Project")}>
          <Select
            showSearch
            allowClear
            disabled={Boolean(project)}
            optionFilterProp="label"
            options={(meta?.projects || []).map(p => ({ value: p._id, label: p.name }))}
            onChange={value => {
              const chosen = meta.projects.find(p => p._id === value);
              if (chosen?.client) form.setFieldsValue({ client: chosen.client });
            }}
          />
        </Form.Item>
        <Form.Item name="offer" label={t("Contract (awarded offer)")}>
          <Select
            showSearch
            allowClear
            optionFilterProp="label"
            options={offers.map(o => ({ value: o._id, label: o.label }))}
          />
        </Form.Item>
        <Form.Item name="client" label={t("Customer")}>
          <Select
            showSearch
            allowClear
            optionFilterProp="label"
            options={(meta?.clients || []).map(c => ({ value: c._id, label: c.name }))}
          />
        </Form.Item>
        <Form.Item name="scope" label={t("Scope")}>
          <Radio.Group
            options={[
              { value: "full", label: t("Entire work") },
              { value: "partial", label: t("Partial acceptance") }
            ]}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

/** Acceptance protocols, optionally of one project. */
const AcceptanceList = ({ meta, project }) => {
  const { t } = useTranslation("dashboard");
  const history = useHistory();
  const isMobile = useIsMobile();
  const [searchText, setSearchText] = useState("");
  const [status, setStatus] = useState("all");
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newOpen, setNewOpen] = useState(false);
  const labels = acceptanceStatusLabels(t);
  const bases = basisLabels(t);

  useEffect(() => {
    setLoading(true);
    listAcceptances({ searchText, status: status === "all" ? undefined : status, project })
      .then(res => setData(res.data.data))
      .catch(generalErrorAlert)
      .finally(() => setLoading(false));
  }, [searchText, status, project]);

  const columns = [
    {
      title: t("Acceptance"),
      key: "number",
      mobile: "title",
      render: (_, item) => (
        <div>
          <Link to={`/user/warranty/acceptances/${item._id}`}>
            <Typography.Text strong>{item.number}</Typography.Text>
          </Link>{" "}
          <Typography.Text type="secondary">
            {formatDay(item.acceptedOn || item.date)}
          </Typography.Text>
          <div className="wty-small wty-clamp">{item.workDescription || item.place}</div>
        </div>
      )
    },
    {
      title: t("Customer"),
      key: "client",
      mobile: "subtitle",
      render: (_, item) =>
        [item.client?.name, item.project?.name].filter(Boolean).join(" · ") || "—"
    },
    {
      title: t("Contract basis"),
      key: "basis",
      render: (_, item) => (
        <span>
          {bases[item.contractBasis]}
          {item.scope === "partial" ? ` · ${t("Partial acceptance")}` : ""}
        </span>
      )
    },
    {
      title: t("Defects"),
      key: "defects",
      align: "right",
      width: 110,
      render: (_, item) =>
        item.defectCounts.total
          ? `${item.defectCounts.total - item.defectCounts.accepted} / ${item.defectCounts.total}`
          : "—"
    },
    {
      title: t("Status"),
      key: "status",
      mobile: "badge",
      width: 170,
      render: (_, item) => (
        <Space size={[4, 4]} wrap>
          <Tag color={ACCEPTANCE_COLORS[item.status]}>{labels[item.status]}</Tag>
          {item.deemed && <Tag>{t("Fictitious")}</Tag>}
          {item.defectCounts.overdue > 0 && <Tag color="error">{t("Overdue defects")}</Tag>}
        </Space>
      )
    }
  ];

  const isEmpty = !loading && !data.length && !searchText && status === "all";
  const table = (
    <ResponsiveTable
      rowKey="_id"
      loading={loading}
      columns={columns}
      dataSource={data}
      mobileAvatar={false}
      onRow={record => ({
        onDoubleClick: () => history.push(`/user/warranty/acceptances/${record._id}`)
      })}
      pagination={data.length > 50 ? { pageSize: 50 } : false}
      scroll={{ x: 760 }}
    />
  );

  return (
    <div>
      <PageToolbar
        filters={
          <Space direction="vertical" className="wty-full">
            <SearchBarComp handleSearch={setSearchText} />
            <Segmented
              value={status}
              onChange={setStatus}
              options={[
                { value: "all", label: t("All") },
                { value: "draft", label: labels.draft },
                { value: "requested", label: labels.requested },
                { value: "accepted", label: labels.accepted },
                { value: "open_defects", label: t("Open defects") }
              ]}
            />
          </Space>
        }
        actions={
          meta?.can.create && (
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setNewOpen(true)}>
              {t("New acceptance")}
            </Button>
          )
        }
      />
      {isEmpty ? (
        <Card>
          <Empty
            description={
              <>
                <Typography.Title level={5}>{t("Acceptance protocols")}</Typography.Title>
                <Typography.Paragraph type="secondary" className="wty-empty-text">
                  {t(
                    "Record the acceptance with defects and reservations, let both parties sign on site or by link. The acceptance date starts the warranty."
                  )}
                </Typography.Paragraph>
              </>
            }
          />
        </Card>
      ) : isMobile ? (
        table
      ) : (
        <Card styles={{ body: { padding: 0 } }}>{table}</Card>
      )}
      <NewAcceptanceModal
        open={newOpen}
        meta={meta}
        project={project}
        onClose={() => setNewOpen(false)}
      />
    </div>
  );
};

export default AcceptanceList;
